import React from 'react';
import {
  createTZDate,
  displayDate,
  displayTime,
  timezoneName,
} from '../../utils/date';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import type z from 'zod';
import type {IEvent} from '../../types/IEvent';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {getReasonFormSchema} from '../reason-form/reason-form.schema';

import {RootMachineContext} from '../../machines/rootMachine/rootMachine.context';
import {selectEventDetails} from '../../selectors/event-details-selector';
import {
  selectError,
  selectIsRescheduleFromContext,
  selectRescheduleLimit,
} from '../../selectors/commonSelector';
import {CardChildren} from '../../lib/card-children/card-children';
import {SmallCard} from '../../lib/small-card/small-card';

import {
  BodyMediumBold,
  BodySmall,
  BodySmallBold,
  Button,
  generateTestId,
  InContextAlert,
  Link,
  SemanticIcon,
  TruncatedTextWithTooltip,
} from '@spaced-out/ui-design-system';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';

import {ReasonInput} from '../reason-form/reason-input';
import {selectReason} from '../../selectors/cancellation-selector';
import {selectTimezone} from '../../selectors/timezoneSelectors';

import css from './booking-success.css';
import {TEST_ID} from '../../utils/testId';


export const BookingSuccess = () => {
  const {t, language} = useLabelTranslation();
  const rootRef = RootMachineContext.useActorRef();
  const state = RootMachineContext.useSelector((state) => state);
  const eventDetails = RootMachineContext.useSelector(
    selectEventDetails,
  ) as IEvent;
  const error = RootMachineContext.useSelector((state) =>
    selectError(state, t),
  );
  const timezone = RootMachineContext.useSelector(selectTimezone) as string;

  const isCancelSubmitting = state.matches('bookingCancelSubmitting');

  const rescheduleLimit = RootMachineContext.useSelector(selectRescheduleLimit);
  const isReschedule = RootMachineContext.useSelector(
    selectIsRescheduleFromContext,
  );
  const reason = RootMachineContext.useSelector(selectReason);

  const showRescheduleLimit = rescheduleLimit > -1 && isReschedule;

  if (!eventDetails) {
    return null;
  }

  const {event_start, event_end} = eventDetails;
  const startDate = createTZDate(event_start, timezone);
  const endDate = createTZDate(event_end, timezone);

  const formattedDate = displayDate(startDate, language);
  const startTime = displayTime(startDate, language);
  const endTime = displayTime(endDate, language);
  const formattedTime = `${startTime} - ${endTime}`;

  const reasonSchema = getReasonFormSchema(t);

  const {control, handleSubmit, formState} = useForm<
    z.infer<typeof reasonSchema>
  >({
    mode: 'onTouched',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(reasonSchema),
  });

  const formattedTimezone = timezoneName(event_start, language);

  const details = [
    {
      label: t('Host', 'Host'),
      value: (
        <BodySmallBold>
          <TruncatedTextWithTooltip
            line={2}
            tooltip={{
              bodyMaxLines: 4,
            }}
          >
            {/* name is not translated */}
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {eventDetails.host_name}
          </TruncatedTextWithTooltip>
        </BodySmallBold>
      ),
      icon: 'user',
    },
    {
      label: t('Date', 'Date'),
      // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
      value: <BodySmallBold>{formattedDate}</BodySmallBold>,
      icon: 'calendar',
    },
    {
      label: t('Time', 'Time'),
      value: (
        <div className={css.timeValue}>
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          <BodySmallBold>{formattedTime}</BodySmallBold>
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          <BodySmall>{formattedTimezone || ''}</BodySmall>
        </div>
      ),
      icon: 'clock',
    },
    {
      label: t('Location', 'Location'),
      value: (
        <BodySmallBold>
          <TruncatedTextWithTooltip
            line={2}
            tooltip={{
              bodyMaxLines: 4,
            }}
          >
            {/* location is a user input */}
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {eventDetails.location || t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </BodySmallBold>
      ),
      icon: 'map-pin',
    },
  ];

  const submitForm = (values: z.infer<typeof reasonSchema>) => {
    rootRef.send({
      type: 'bookingCancelForm.submit',
      reason: values.reason || '',
    });
  };

  const isCancel = state.matches('bookingCancelForm');
  const {errorMessage, allowRetry, isError} = error;

  return (
    <SmallCard
      title={
        isReschedule
          ? t(
              'Your_meeting_has_been_rescheduled_successfully',
              'Your meeting has been rescheduled successfully',
            )
          : `${t(
              'Thank_you_your_meeting_is_confirmed',
              'Thank you, your meeting is confirmed',
            )}!`
      }
      icon="badge-check"
      iconSemantic="success"
      testId={generateTestId({
        base: TEST_ID.BOOKING_SUCCESS,
        slot: 'booking-success-small-card',
      })}
    >
      <div className={css.detailsWrapper}>
        {showRescheduleLimit && (
          <InContextAlert
            semantic="success"
            testId={generateTestId({
              base: TEST_ID.BOOKING_SUCCESS,
              slot: 'in-context-alert-success',
            })}
          >{`${t(
            'Your_meeting_has_been_rescheduled_You_have_rescheduleLimit_reschedules_left',
            {rescheduleLimit},
          )}. `}</InContextAlert>
        )}
        <CardChildren
          testId={generateTestId({
            base: TEST_ID.BOOKING_SUCCESS,
            slot: 'card-children',
          })}
        >
          <div className={css.details}>
            {details.map(({icon, label, value}) => (
              <div key={label} className={css.detailItem}>
                <div className={css.detailItemLabel}>
                  <SemanticIcon
                    size="small"
                    name={icon}
                    semantic="neutral"
                    type="solid"
                  />
                  {/* label is already translated */}
                  {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                  <BodyMediumBold>{label}</BodyMediumBold>
                </div>
                {/* value is a react component */}
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                <div className={css.detailItemValue}>{value}</div>
              </div>
            ))}
          </div>
        </CardChildren>
        <CardChildren
          testId={generateTestId({
            base: TEST_ID.BOOKING_SUCCESS,
            slot: 'card-children-2',
          })}
        >
          {isCancel || isCancelSubmitting || isError ? (
            <div
              className={css.cancelForm}
              data-testid={generateTestId({
                base: TEST_ID.BOOKING_SUCCESS,
                slot: 'cancel-form',
              })}
            >
              {isError && (
                <InContextAlert
                  semantic="danger"
                  testId={generateTestId({
                    base: TEST_ID.BOOKING_SUCCESS,
                    slot: 'in-context-alert-danger',
                  })}
                >
                  {/* Note: already getting translated from errorText */}
                  {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                  {errorMessage}
                </InContextAlert>
              )}
              <ReasonInput
                control={control}
                reasonName="reason"
                required={true}
                disabled={isCancelSubmitting}
                actionType="cancel"
                participantType="CANDIDATE"
                testId={generateTestId({
                  base: TEST_ID.BOOKING_SUCCESS,
                  slot: 'reason-input',
                })}
              />
              <Button
                onClick={handleSubmit(submitForm)}
                isLoading={isCancelSubmitting}
                disabled={!formState.isValid || !allowRetry}
                testId={generateTestId({
                  base: TEST_ID.BOOKING_SUCCESS,
                  slot: 'button',
                })}
              >
                {t('Cancel', 'Cancel')}
              </Button>
            </div>
          ) : (
            <div className={css.successFooterContainer}>
              {isReschedule && (
                <Textarea
                  locked={true}
                  testId={generateTestId({
                    base: TEST_ID.BOOKING_SUCCESS,
                    slot: 'reschedule-textarea',
                  })}
                  value={reason || ''}
                  label={t('Reason_for_reschedule', 'Reason for reschedule')}
                />
              )}
              <div className={css.successFooter}>
                <BodySmall>
                  {t('Need_to_Make_Changes', 'Need to Make Changes')}?
                </BodySmall>
                <Link
                  onClick={() => rootRef.send({type: 'reschedule.go'})}
                  testId={generateTestId({
                    base: TEST_ID.BOOKING_SUCCESS,
                    slot: 'link',
                  })}
                >
                  {t('Reschedule', 'Reschedule')}
                </Link>
                <BodySmall>{t('or', 'or')}</BodySmall>
                <Link
                  onClick={() => rootRef.send({type: 'cancellation.go'})}
                  testId={generateTestId({
                    base: TEST_ID.BOOKING_SUCCESS,
                    slot: 'cancel-link',
                  })}
                >
                  {t('Cancel', 'Cancel')}
                </Link>
              </div>
            </div>
          )}
        </CardChildren>
      </div>
    </SmallCard>
  );
};
